import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    IconButton,
    Typography,
    Slide
} from '@mui/material';
import {
    Close as CloseIcon,
    GetApp as GetAppIcon,
    PhoneIphone as PhoneIcon
} from '@mui/icons-material';

interface BeforeInstallPromptEvent extends Event {
    prompt: () => Promise<void>;
    userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const PWAInstallPrompt: React.FC = () => {
    const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
    const [showPrompt, setShowPrompt] = useState(false);
    const [isInstalled, setIsInstalled] = useState(false);
    const [isIOS, setIsIOS] = useState(false);
    const [isStandalone, setIsStandalone] = useState(false);

    useEffect(() => {
        // Detect iOS
        const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
        setIsIOS(iOS);

        // Check if already installed
        const standalone = window.matchMedia('(display-mode: standalone)').matches ||
            (window.navigator as any).standalone === true;
        setIsStandalone(standalone);

        if (standalone) {
            setIsInstalled(true);
            return;
        }

        // Check if user has dismissed the prompt before
        const dismissed = localStorage.getItem('pwa_install_dismissed');
        if (dismissed) {
            const dismissedTime = parseInt(dismissed);
            const daysSinceDismissed = (Date.now() - dismissedTime) / (1000 * 60 * 60 * 24);
            if (daysSinceDismissed < 7) {
                // Don't show again for 7 days
                return;
            }
        }

        // For iOS, show the prompt after a delay (since there's no beforeinstallprompt event)
        if (iOS) {
            setTimeout(() => {
                setShowPrompt(true);
            }, 5000); // Show after 5 seconds on iOS
            return;
        }

        const handleBeforeInstallPrompt = (e: Event) => {
            // Prevent the mini-infobar from appearing on mobile
            e.preventDefault();

            // Stash the event so it can be triggered later
            setDeferredPrompt(e as BeforeInstallPromptEvent);

            // Show the install prompt after a short delay
            setTimeout(() => {
                setShowPrompt(true);
            }, 3000); // Show after 3 seconds
        };

        window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

        // Check if app was installed
        window.addEventListener('appinstalled', () => {
            console.log('PWA was installed');
            setIsInstalled(true);
            setShowPrompt(false);
        });

        return () => {
            window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
        };
    }, []);

    const handleInstallClick = async () => {
        if (!deferredPrompt) {
            return;
        }

        // Show the install prompt
        await deferredPrompt.prompt();

        // Wait for the user to respond to the prompt
        const { outcome } = await deferredPrompt.userChoice;

        console.log(`User response to the install prompt: ${outcome}`);

        if (outcome === 'accepted') {
            console.log('User accepted the install prompt');
        } else {
            console.log('User dismissed the install prompt');
        }

        // Clear the deferredPrompt
        setDeferredPrompt(null);
        setShowPrompt(false);
    };

    const handleDismiss = () => {
        setShowPrompt(false);
        localStorage.setItem('pwa_install_dismissed', Date.now().toString());
    };

    if (isInstalled || !showPrompt) {
        return null;
    }

    return (
        <Slide direction="up" in={showPrompt} mountOnEnter unmountOnExit>
            <Box
                sx={{
                    position: 'fixed',
                    bottom: 16,
                    left: 16,
                    right: 16,
                    zIndex: 9998,
                    maxWidth: 400,
                    mx: 'auto'
                }}
            >
                <Card
                    elevation={8}
                    sx={{
                        bgcolor: 'primary.main',
                        color: 'primary.contrastText'
                    }}
                >
                    <CardContent sx={{ position: 'relative', pb: 2 }}>
                        <IconButton
                            size="small"
                            onClick={handleDismiss}
                            sx={{
                                position: 'absolute',
                                top: 8,
                                right: 8,
                                color: 'inherit'
                            }}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                            <PhoneIcon sx={{ fontSize: 40 }} />
                            <Box>
                                <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                                    {isIOS ? 'Add to Home Screen' : 'Install P2P Exchange'}
                                </Typography>
                                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                    {isIOS
                                        ? 'Use Safari\'s share button to add this app to your home screen'
                                        : 'Access currency rates offline anytime!'
                                    }
                                </Typography>
                            </Box>
                        </Box>

                        {isIOS && (
                            <Box sx={{
                                bgcolor: 'rgba(255, 255, 255, 0.1)',
                                borderRadius: 1,
                                p: 2,
                                mb: 2,
                                border: '1px solid rgba(255, 255, 255, 0.2)'
                            }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: 'white' }}>
                                    📱 How to install on iPhone:
                                </Typography>
                                <Box component="ol" sx={{ pl: 2, m: 0, color: 'white', fontSize: '0.875rem' }}>
                                    <li>Tap the <strong>Share</strong> button (□↗) at the bottom</li>
                                    <li>Scroll down and tap <strong>"Add to Home Screen"</strong></li>
                                    <li>Tap <strong>"Add"</strong> to confirm</li>
                                </Box>
                            </Box>
                        )}

                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                                variant="contained"
                                fullWidth
                                onClick={isIOS ? handleDismiss : handleInstallClick}
                                startIcon={<GetAppIcon />}
                                sx={{
                                    bgcolor: 'white',
                                    color: 'primary.main',
                                    '&:hover': {
                                        bgcolor: 'grey.100'
                                    }
                                }}
                            >
                                {isIOS ? 'I understand' : 'Install App'}
                            </Button>
                            <Button
                                variant="text"
                                onClick={handleDismiss}
                                sx={{
                                    color: 'white',
                                    '&:hover': {
                                        bgcolor: 'rgba(255, 255, 255, 0.1)'
                                    }
                                }}
                            >
                                Later
                            </Button>
                        </Box>

                        <Typography
                            variant="caption"
                            sx={{
                                display: 'block',
                                mt: 1.5,
                                opacity: 0.8,
                                textAlign: 'center'
                            }}
                        >
                            {isIOS
                                ? '📱 Use Safari browser for best experience'
                                : '✓ Works offline  ✓ Fast loading  ✓ No app store needed'
                            }
                        </Typography>
                    </CardContent>
                </Card>
            </Box>
        </Slide>
    );
};

export default PWAInstallPrompt;
